import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { RecRefs } from "./RecRefs";

/*
 * The tiny (i) of an AI Recommendation (owner 10-06: "hide source links inside
 * a tiny (i) button so that we don't inundate viewer with useless info").
 *
 *   button   a real <button> right after the title (glued to its last word),
 *            aria-label "Sources", aria-haspopup / aria-expanded / aria-controls,
 *            a 32 px hit area (40 px on touch).
 *   popover  the full sentence (when the title shortens it), the item's records
 *            (they open in the same lab popup / document viewer as before), its
 *            guideline link (new tab), the order its Actions cell places (or why
 *            there is none: careiq.n3.4 no_order_reason) and one grey provenance
 *            line. Opening it
 *            moves focus to its ✕; Esc, ✕, a click outside or focus leaving it
 *            closes it, and Esc / ✕ give focus back to the (i).
 *
 * Placement: position fixed, measured from the (i), so the drawer's and the
 * overlay's scroll boxes never clip it; it flips above when there is no room
 * below and follows scrolling. It is rendered inside the item's card, so inside
 * "Show all" it is inside #n3-ovl and a click in it never closes the overlay.
 * Esc is taken in the capture phase while it is open, so the overlay (and any
 * other Esc listener) never sees it. A record opened from it closes it first and
 * gives focus back to the (i), so the viewer's own Esc / focus return work as
 * before. Each card owns its own state: the hidden morph copy of the drawer
 * renders closed and holds no ids.
 */

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5" />
    <circle cx="12" cy="7.6" r=".6" fill="currentColor" />
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const GAP = 8;        // between the (i) and the popover
const EDGE = 12;      // from the viewport edge
const WIDTH = 340;

export function useInfo() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const id = useId();
  const close = useCallback((refocus) => {
    setOpen(false);
    if (refocus && btnRef.current) btnRef.current.focus({ preventScroll: true });
  }, []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  return { open, toggle, close, btnRef, popRef, id: `n3-info-${String(id).replace(/[^a-zA-Z0-9_-]/g, "")}` };
}

export function InfoButton({ info, label = "Sources" }) {
  return (
    <button
      type="button"
      ref={info.btnRef}
      className="n3-ib-i no-hover-tint"
      aria-label={label}
      title={info.open ? undefined : label}
      aria-haspopup="dialog"
      aria-expanded={info.open}
      aria-controls={info.open ? info.id : undefined}
      data-act="info"
      onClick={info.toggle}
    >
      <InfoIcon />
    </button>
  );
}

const place = (btn, pop) => {
  if (!btn || !pop) return;
  const r = btn.getBoundingClientRect();
  const vw = document.documentElement.clientWidth || window.innerWidth || 0;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  const w = Math.max(200, Math.min(WIDTH, vw - 2 * EDGE));
  let left = r.left + r.width / 2 - 24;
  left = Math.max(EDGE, Math.min(left, vw - EDGE - w));
  pop.style.width = `${w}px`;
  pop.style.maxHeight = "";
  const h = pop.offsetHeight;
  const below = vh - r.bottom - GAP - EDGE;
  const above = r.top - GAP - EDGE;
  const flip = h > below && above > below;
  const room = Math.max(160, flip ? above : below);
  if (h > room) pop.style.maxHeight = `${room}px`;
  const hh = Math.min(h, room);
  let top = flip ? r.top - GAP - hh : r.bottom + GAP;
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;
  // a transformed ancestor would offset a fixed box: correct by what was measured
  const got = pop.getBoundingClientRect();
  if (Math.abs(got.left - left) > 0.5 || Math.abs(got.top - top) > 0.5) {
    pop.style.left = `${left - (got.left - left)}px`;
    top -= got.top - top;
    pop.style.top = `${top}px`;
  }
  pop.dataset.side = flip ? "above" : "below";
  pop.style.setProperty("--n3-caret", `${Math.max(14, Math.min(w - 14, r.left + r.width / 2 - left))}px`);
};

export function InfoPopover({ info, rec, title, detail, order = null, prov, refYear, onOpenEvidence }) {
  const { open, close, btnRef, popRef, id } = info;
  const closeRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return;
    place(btnRef.current, popRef.current);
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
  }, [open, btnRef, popRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close(true);
    };
    const onDown = (e) => {
      const t = e.target;
      if ((popRef.current && popRef.current.contains(t)) || (btnRef.current && btnRef.current.contains(t))) return;
      close(false);
    };
    const onMove = () => place(btnRef.current, popRef.current);
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    document.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, close, btnRef, popRef]);

  if (!open) return null;
  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (!next) return;
    if ((popRef.current && popRef.current.contains(next)) || (btnRef.current && btnRef.current.contains(next))) return;
    close(false);
  };
  const openRecord = (r) => {
    close(true);
    if (onOpenEvidence) onOpenEvidence(r);
  };
  return (
    <div
      ref={popRef}
      id={id}
      className="n3-pop"
      role="dialog"
      aria-label={`Sources: ${title}`}
      data-pop={rec.issue_key}
      onBlur={onBlur}
    >
      <button type="button" ref={closeRef} className="n3-pop-x no-hover-tint" aria-label="Close sources" onClick={() => close(true)}>
        <XIcon />
      </button>
      {detail ? <p className="n3-pop-det">{detail}</p> : null}
      <RecRefs item={rec} refYear={refYear} onOpenEvidence={openRecord} className="n3-brow-refs" />
      {order ? (
        <div className="n3-pop-ord" data-ord={order.k === "Order" ? "order" : "none"}>
          <span className="n3-ref-k">{order.k}</span>
          <span className={`n3-pop-ord-v${order.k === "Order" ? " o" : ""}`}>{order.v}</span>
          {order.t ? <span className="n3-pop-ord-t"> · {order.t}</span> : null}
        </div>
      ) : null}
      {prov ? <div className="n3-pop-prov">{prov}</div> : null}
    </div>
  );
}
